import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  Form,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExistingEmployeeSelect } from "@common/components/EmployeeSelect/ExistingEmployeeSelect";
import {
  User,
  UserRole,
  useAdminDepartmentsQuery,
} from "@common/utils/graphql";
import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useRecoilValue } from "recoil";
import { viewPersonManager } from "./ViewPersonContent";
import { SelectUserRoles, UserRoleOption } from "./SelectUserRoles";
import { roleState } from "@common/state/roleState";
import { useUserStore } from "@domains/auth/utils/useUserStore";

interface Props {
  form: UseFormReturn<any>;
  isLoading: boolean;
  isEditMode: boolean;
  // undefined if its invite mode, edit mode has the userId
  userId?: string;
}

const adminRoleOptions: UserRoleOption[] = [
  { role: UserRole.Employee, disabled: true },
  { role: UserRole.Manager },
  { role: UserRole.Admin },
] as const;

const managerRoleOptions: UserRoleOption[] = [
  { role: UserRole.Employee, disabled: true },
  { role: UserRole.Manager },
  { role: UserRole.Admin, disabled: true },
] as const;

export const PersonManualForm = ({
  form,
  isLoading,
  isEditMode,
  userId,
}: Props) => {
  const { loading: departmentsLoading, data: departmentsData } =
    useAdminDepartmentsQuery();
  const role = useRecoilValue(roleState);

  // The persons's manager, populated before entering the edit person form
  let defaultManager = useRecoilValue(viewPersonManager) as Pick<
    User,
    "displayName"
  >;
  const displayName = useUserStore((state) => state.displayName);
  const isManager = role === UserRole.Manager;

  // When a manager is inviting a person, set them as the person's manager and prevent it from being changed
  const disableManagerSelect = isManager && !isEditMode && !!displayName;
  if (disableManagerSelect) {
    defaultManager = { displayName };
  }

  // Managers can't assign an Admin role
  const roleOptions = isManager ? managerRoleOptions : adminRoleOptions;

  const departments = useMemo(() => {
    if (
      departmentsData?.departments?.__typename !== "QueryDepartmentsSuccess"
    ) {
      return [];
    }
    return departmentsData?.departments?.data.departments;
  }, [departmentsData]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(() => {})} className="space-y-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="First Name"
                  disabled={isLoading}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="Last Name"
                  disabled={isLoading}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="workEmail"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Work Email</FormLabel>
              <FormControl>
                <Input
                  placeholder="Work Email"
                  disabled={isLoading || isEditMode}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="departmentId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Department</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={
                  departmentsLoading || isLoading || role === UserRole.Manager
                }
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem
                      key={`department-list-${d.id}`}
                      value={String(d.id)}
                    >
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          disabled={isLoading}
          name="managerEmployeeProfileId"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex justify-between">Manager</FormLabel>
              <FormControl>
                <ExistingEmployeeSelect
                  placeholder={"Select manager"}
                  defaultValue={defaultManager}
                  disabled={disableManagerSelect}
                  onChange={(e) => {
                    // mutation uses employeeProfileId here, not userId
                    field.onChange(e.employeeProfileId);
                  }}
                  exceptUserId={userId}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="roles"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role</FormLabel>
              <SelectUserRoles
                userRoles={form.watch("roles")}
                onChange={field.onChange}
                options={roleOptions}
              />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};
